import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ui/theme-toggle";
import LanguageToggle from "@/components/ui/language-toggle";
import { useLanguage } from "@/contexts/LanguageContext";

const Navigation = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { label: t("nav.skills"), href: "#skills" },
    { label: t("nav.experience"), href: "#experience" },
    { label: t("nav.portfolio"), href: "#portfolio" },
    { label: t("nav.certifications"), href: "#certifications" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header role="banner">
      <nav
        aria-label={t("nav.main") || "Navigation principale"}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-luxury ${
          isScrolled
            ? "bg-background/85 backdrop-blur-2xl border-b border-primary/8 shadow-luxury"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Refined Luxury style */}
            <a 
              href="#" 
              className="group flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg py-2"
              aria-label="Aurélia Ravelo - Accueil"
            >
              {/* Decorative monogram */}
              <div className="relative w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center bg-gradient-to-br from-primary/10 to-transparent group-hover:border-primary/50 group-hover:shadow-glow-soft transition-all duration-500">
                <span className="text-lg font-serif font-semibold text-primary">A</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-serif font-semibold tracking-tight text-foreground group-hover:text-primary transition-colors duration-500">
                  Aurélia Ravelo
                </span>
                <div className="h-px w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" />
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1" role="menubar">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  role="menuitem"
                  className="relative px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg group link-glow"
                >
                  <span className="relative z-10">{item.label}</span>
                  {/* Elegant hover background */}
                  <span className="absolute inset-0 rounded-lg bg-primary/0 group-hover:bg-primary/5 dark-glow-hover transition-colors duration-300" />
                  {/* Underline accent */}
                  <span className="absolute bottom-1.5 left-5 right-5 h-px bg-gradient-to-r from-primary via-primary to-primary/30 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </a>
              ))}
              
              <div className="ml-6 flex items-center gap-3 pl-6 border-l border-primary/10">
                <LanguageToggle />
                <ThemeToggle />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 md:hidden">
              <LanguageToggle />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                className="w-10 h-10 rounded-full hover:bg-primary/8 border border-transparent hover:border-primary/20 transition-all duration-300"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Menu className="w-5 h-5" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation - Enhanced */}
          <div 
            id="mobile-menu" 
            className={`md:hidden overflow-hidden transition-all duration-500 ease-luxury ${
              isMobileMenuOpen ? "max-h-96 opacity-100 pb-6" : "max-h-0 opacity-0"
            }`}
            role="menu"
            aria-label="Menu mobile"
          >
            <div className="pt-4 border-t border-primary/10">
              <div className="flex flex-col gap-1">
                {navItems.map((item, index) => (
                  <a
                    key={item.label}
                    href={item.href}
                    role="menuitem"
                    className={`px-5 py-3.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary border border-transparent hover:border-primary/10 ${
                      isMobileMenuOpen ? "animate-fade-in" : ""
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{ 
                      animationDelay: isMobileMenuOpen ? `${index * 60}ms` : "0ms",
                    }}
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
                      {item.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;